import {Box,styled} from "@mui/material";
import Base from "../../../../../components/base";
import {
  addMinutesToTimeString,
  buildPath,
  formatDateInfo,
  formatTime,
  getFasciaRange,
  scale,
  timeStringToDayJs
} from "../../../../../utils";
import BaseHeader from "../../../../../components/base/header";
import BaseTitle from "../../../../../components/base/title";
import LABEL from "../../../../../resources/label";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import DateDetailedCard from "../../../../../components/date-detailed-card";
import Text from "../../../../../components/text";
import theme, {FONTS} from "../../../../../constants/theme";
import React, {useEffect, useState} from "react";
import DateRowInfo from "../../../../../components/date-row-info";
import CustomSvg from "../../../../../components/custom-svg";
import {GOCCIA_RIFLESSO} from "../../../../../constants/svgs";
import TimeBox from "../../../../../components/time-box";
import {fetchRaccoltaById} from "../../../../../features/operator";
import {FasciaETurniResponse, NewDonorRequest, RaccoltaResponse, TurnoResponse} from "../../../../../types";
import {useMediaQuery} from "@mui/system";

const Wrapper = styled(Box)({

});

const ContentWrapper = styled(Box)({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-around",
  marginTop: scale(65),
  height: scale(480 + 138),
  width: scale(320 + 40 + 753.14),
  [theme.breakpoints.down('sm')]: {
    width: '100vw',
    marginTop: 0,
    height: scale(684),
  },
});

const DonorsWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  position: 'relative',
  top: scale(16),
  width: scale(730),
  height: scale(79 + 515),
  gap: scale(20),
});

const ChoiceRow = styled(Box)({
  display: 'flex',
  flexDirection: "column",
  width: scale(654.5),
  height: scale(213),
  marginTop: scale(10),
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'flex-start',
  },
});

const DetailBand = styled(Box)({
  display: 'flex',
  flexDirection: "column",
  gap: scale(12),
  marginTop: scale(12),
  [theme.breakpoints.down('sm')]: {
    justifyContent: "flex-start",
    alignItems: 'center',
    flexDirection: "column",
    marginTop: 0,
    alignSelf: 'baseline',
    textAlign: 'center',
  },
});

const HourGroup = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  gap: scale(20),
  maxHeight: `calc(4 * ${scale(90)} + ${scale(20)} * 3)`,
  overflowY: 'auto',
  padding: scale(4),
  boxSizing: 'content-box',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    maxHeight: `calc(6 * ${scale(90)} + ${scale(20)} * 3)`,
  },
});

const OperatoreSelezioneOrari = () => {
  const { raccoltaId } = useParams<{ raccoltaId: string }>();
  const navigate = useNavigate();
  const [raccolta, setRaccolta] = useState<RaccoltaResponse | null>(null)
  const [partialDonorInfo, setPartialDonorInfo] = useState<Partial<NewDonorRequest> | null>(null)
  const [turni, setTurni] = useState<TurnoResponse[] | null>(null)
  const [turno, setTurno] = useState<TurnoResponse | null>(null)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const location = useLocation();
  const path = location.pathname;

  const isOperatore = path.includes('operatore');
  const nextUrl = isOperatore ? `/operatore/dettaglio-raccolta/${raccoltaId}/aggiungi-donatore/selezione-orari/riepilogo-prenotazione` : `/donatore/raccolta/${raccoltaId}/selezione-orari/riepilogo-prenotazione`


  useEffect(() => {
    fetchRaccoltaById(Number(raccoltaId)).then((res) => {
      setRaccolta(res);
      const stored = sessionStorage.getItem('newDonorInfo')
      if (stored) {
        setPartialDonorInfo(JSON.parse(stored))
        const fascia: FasciaETurniResponse | null = res.fasceETurni.find(el => el.fascia.id === partialDonorInfo?.id_fascia) ?? null
        if (fascia) {
          let turniList: TurnoResponse[] = fascia.turni.filter(el => el.type === partialDonorInfo?.type)
          turniList = turniList.slice().sort((a, b) => {
              return timeStringToDayJs(formatTime(a.start)).diff(timeStringToDayJs(formatTime(b.start)));
            }
          );
          setTurni(turniList)
        }
      } else {
        //TODO
        navigate(isOperatore ? buildPath(`/operatore/dettaglio-raccolta/${raccoltaId}/aggiungi-donatore`) : buildPath(`/donatore/raccolta/${raccoltaId}`))
      }
    })

  }, [isOperatore, navigate, partialDonorInfo?.id_fascia, partialDonorInfo?.type, raccoltaId])

  if (!raccolta || !partialDonorInfo || !turni) {
    return null;
  }

  const dateInfo = formatDateInfo(raccolta.date)

  const updateDonorInfo = (item: TurnoResponse) => {
    if (item) {
      const info: Partial<NewDonorRequest> = {
        ...partialDonorInfo,
        id_turno: item.id
      }
      sessionStorage.setItem('newDonorInfo', JSON.stringify(info));
    }
  }


  return (
    <Wrapper>
      <Base style={{ height: isMobile ? 'auto' : scale(850), width: isMobile ? undefined : scale(1200)}}>
        <BaseHeader/>
        <BaseTitle
          title={
            isOperatore ? LABEL.DETAIL_DONATION : 'Orari raccolta '.concat(partialDonorInfo.type === 'sangue' ? 'sangue' : 'plasma')
          }
        />
        <ContentWrapper>
          {!isMobile && (<Box style={{display: 'flex', flexDirection: 'column', gap: scale(20)}}>
            <DateDetailedCard
              dayName={dateInfo.weekday}
              dayNumber={dateInfo.day.toString()}
              month={dateInfo.month.concat('\n')}
              year={dateInfo.year.toString()}
              colorStyle={"secondary"}
              city={raccolta.name}
              firstHour={getFasciaRange(raccolta, 0)}
              secondHour={getFasciaRange(raccolta, 1)}
              address={raccolta.address}
              sx={{paddingBottom: raccolta.name.length > 14 ? scale(20) : 0}}
            />
            {(raccolta.type === 'sangue' || raccolta.type === 'plasma & sangue') && (<DateRowInfo
              IconComponent={
                <CustomSvg
                  svg={GOCCIA_RIFLESSO}
                  sx={{
                    zIndex: 2,
                    fontSize: scale(36),
                  }}
                  color={'white'}
                />
              }
              bgColor={theme.palette.blood.main}
              colorStyle={'primary'}
              firstRowText={(raccolta.postiDisponibili ? raccolta.postiDisponibili.toString().concat(' ') : '') + 'Postazioni'}
              secondRowText={'Sangue'}
            />)}
            {(raccolta.type === 'plasma' || raccolta.type === 'plasma & sangue') && (<DateRowInfo
              IconComponent={
                <CustomSvg
                  svg={GOCCIA_RIFLESSO}
                  sx={{
                    zIndex: 2,
                    fontSize: scale(36),
                  }}
                  color={'white'}
                />
              }
              bgColor={theme.palette.plasma.main}
              colorStyle={'primary'}
              firstRowText={(raccolta.postiDisponibili ? raccolta.postiDisponibili.toString().concat(' ') : '') + 'Postazioni'}
              secondRowText={'Plasma'}
            />)}
          </Box>)}
          <DonorsWrapper>
            <ChoiceRow>
              <Box style={{display: 'flex', flexDirection: 'column'}}>
                {!isMobile && (<Text
                  text={'Scegli la fascia oraria'}
                  color={theme.palette.text.secondary}
                  fontFamily={FONTS.MONTSERRAT}
                  fontWeight={700}
                  fontSize={scale(24)}
                  sx={{textTransform: 'uppercase', alignSelf: 'flex-start'}}
                />)}
                {!isMobile && (<Box
                  sx={{
                    backgroundColor: theme.palette.text.secondary,
                    height: scale(2),
                    width: '100%',
                    marginTop: scale(10),
                  }}
                />)}
              </Box>
              <DetailBand>
                <Text
                  text={
                    'La durata del prelievo è di circa ' +
                    (partialDonorInfo.type === 'sangue' ? '10' : '45') +
                    ' minuti'
                  }
                  color={theme.palette.ceramic.contrastText}
                  fontFamily={FONTS.MONTSERRAT}
                  fontWeight={400}
                  fontSize={scale(20)}
                  sx={{alignSelf: 'flex-start', width: scale(608)}}
                />
                <HourGroup>
                  {turni.map((item, index) => {
                    const isSelected = turno?.id === item.id;
                    return (
                      <TimeBox
                        key={index}
                        firstRowText={formatTime(item.start)}
                        secondRowText={
                          partialDonorInfo.type === 'plasma'
                            ? formatTime(addMinutesToTimeString(item.start, 45))
                            : ''
                        }
                        isDisabled={item.postiDisponibili <= 0}
                        sx={{
                          cursor: item.postiDisponibili <= 0 ? 'not-allowed' : 'pointer',
                          backgroundColor: item.postiDisponibili > 0 ? theme.palette.success.main : theme.palette.grey[400],
                          color: isSelected ? 'white' : 'inherit',
                          width: isMobile ? scale(30) : undefined,
                          height: isMobile ? scale(30) : undefined,
                          minWidth: isMobile ? scale(135) : undefined,
                          minHeight: isMobile ? scale(73) : undefined,
                          borderRadius: isMobile ? scale(20) : undefined,
                        }}
                        textSx={{fontSize: isMobile ? scale(20) : scale(18)}}
                        onClick={() => {
                          if (item.postiDisponibili > 0) {
                            setTurno(item);
                            updateDonorInfo(item)
                            navigate(buildPath(nextUrl))
                          }
                        }}
                      />
                  )})}
                </HourGroup>
              </DetailBand>
            </ChoiceRow>
          </DonorsWrapper>
        </ContentWrapper>
      </Base>
    </Wrapper>
  );
}

export default OperatoreSelezioneOrari;
